import React, {useEffect, useMemo, useState} from 'react';
import {CasinoSelectItem} from '@/common-pages/casino/casino-select';
import {
  getGameProvider,
  NewGameListItem,
  NewGameListParams,
  postNewGameList,
} from '@/common-pages/casino/casino.service';
import {FilterModalOption} from '@basicComponents/filter-modal';
import {sortBy} from '@/common-pages/live-casino/live-casino-service';
import {toGame} from '@/common-pages/game-navigate';
import {GamePicType, getGamePicUrl} from '@/components/utils';

type AnnaLiveGameItem = NewGameListItem & {
  playersNumber?: number;
};

const AnnaHomeLive = () => {
  const [providerOptions, setProviderOptions] = useState<FilterModalOption[]>([
    {
      title: 'All',
      value: '',
    },
  ]);
  const [gameType, setGameType] = useState('');
  const [sort, setSort] = useState('');
  const [gameList, setGameList] = useState<AnnaLiveGameItem[]>([]);
  const [totalSize, setTotalSize] = useState(0);

  const sortOptions = useMemo(
    () =>
      sortBy.map(item => ({
        title: item.label,
        value: item.value,
      })),
    [],
  );

  useEffect(() => {
    let cancelled = false;

    getGameProvider('liveCasino')
      .then(res => {
        if (cancelled) {
          return;
        }

        setProviderOptions([
          {
            title: 'All',
            value: '',
          },
          ...res.map(item => ({
            title: item.name,
            value: String(item.id),
          })),
        ]);
      })
      .catch(() => undefined);

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const params: NewGameListParams = {
      l1CategoryId: 3,
      pageNo: 1,
      pageSize: 30,
    };

    if (gameType) {
      params.l2CategoryId = gameType;
    }

    if (sort) {
      params.orderByField = 'game_type';
      params.isAsc = sort === 'A-Z';
    }

    postNewGameList(params)
      .then(res => {
        if (cancelled) {
          return;
        }

        setGameList((res.content || []) as AnnaLiveGameItem[]);
        setTotalSize(res.totalSize || res.content?.length || 0);
      })
      .catch(() => {
        if (cancelled) {
          return;
        }

        setGameList([]);
        setTotalSize(0);
      });

    return () => {
      cancelled = true;
    };
  }, [gameType, sort]);

  return (
    <div>
      <div className="flex h-14 items-center justify-between bg-[linear-gradient(180deg,#FFFFFF_0%,#F6F7FA_100%)] px-3 py-3">
        <CasinoSelectItem
          optionsList={providerOptions}
          name="Categories"
          onChange={value => setGameType(String(value || ''))}
        />
        <CasinoSelectItem
          optionsList={sortOptions}
          name="Sort"
          onChange={value => setSort(String(value || ''))}
        />
      </div>

      <div className="flex flex-wrap px-3">
        {gameList.map((item, index) => (
          <div
            key={item.id}
            onClick={() =>
              toGame({
                source: item.source,
                name: item.name,
                gameUrl: item.gameUrl,
                id: item.id,
                tripartiteUniqueness: item.tripartiteUniqueness,
              })
            }
            className={`mb-1.5 w-[calc((100%-0.75rem)/3)] cursor-pointer rounded-lg ${
              (index + 1) % 3 ? 'mr-1.5' : ''
            }`}>
            <div className="relative h-[7.0625rem] w-full overflow-hidden rounded-t-lg">
              <div
                className="h-full w-full bg-cover bg-center bg-no-repeat"
                style={{
                  backgroundImage: `url(${getGamePicUrl(
                    GamePicType.SQUARE,
                    item.source,
                    item.tripartiteUniqueness,
                  )})`,
                }}
              />
            </div>
            <div className="h-[2.622rem] rounded-b-lg bg-white px-2 pb-1 pt-1">
              <div
                className="mb-1 w-[6rem] truncate text-[0.75rem] font-medium leading-none text-[#31373D]"
                title={item.name}>
                {item.name}
              </div>
              <div className="flex flex-row items-center">
                <div className="truncate text-[0.625rem] font-medium leading-none tracking-[-0.0125rem] text-[#5F6975]">
                  {item.provider}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      {gameList.length > 0 ? (
        <div className="mt-1.5 flex w-full flex-col items-center pb-3">
          <div className="mb-2 text-[0.75rem] font-medium text-[#31373D]">
            {gameList.length} / {totalSize || gameList.length}
          </div>
          {gameList.length >= (totalSize || gameList.length) ? (
            <div className="text-[0.875rem] font-normal text-[#9FA5AC]">
              No more data
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

export default AnnaHomeLive;
